import React, { useState, ReactNode } from 'react';
import { Button, Dialog, Box, Typography, Stack, Divider, IconButton, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { Campaign } from '@app-types/models/user';

interface MissionUser {
  role: string;
  status?: string;
}

interface MissionProps {
  numberOfWeeksHasPastBy: number;
  weeklyShowcaseCount: number;
  timeUntilNextWeek: string;
  campaigns: Campaign[];
  user: MissionUser;
}

interface MissionGroupProps {
  title: string;
  children: ReactNode;
}

interface CompulsoryMissionProps {
  numberOfWeeksHasPastBy?: number;
  weeklyShowcaseCount: number;
  timeUntilNextWeek: string;
}

interface CompulsoryMissionCardProps {
  title: string;
  description: string;
  progressCount: number;
  progressTarget: number;
  progressColor: string;
  timeRemaining: string;
  isCompleted: boolean;
}

interface RewardMissionCardProps {
  campaign: Campaign;
}

const Mission: React.FC<MissionProps> = ({ numberOfWeeksHasPastBy, weeklyShowcaseCount, timeUntilNextWeek, campaigns, user }) => {
  const { t } = useTranslation();

  return (
    <Box className="mission">
      {user.role === 'stylist' && user.status === 'listing' && (
        <MissionGroup title={t('mission.compulsoryMissionTitle')}>
          <Stack spacing={2}>
            <Box sx={{ fontWeight: 'bold' }}>{t('mission.compulsoryMissionDescription')}</Box>
            <CompulsoryMission
              numberOfWeeksHasPastBy={numberOfWeeksHasPastBy}
              weeklyShowcaseCount={weeklyShowcaseCount}
              timeUntilNextWeek={timeUntilNextWeek}
            />
          </Stack>
        </MissionGroup>
      )}
      <MissionGroup title={t('mission.rewardMissions')}>
        {campaigns.map((campaign, index) => (
          <React.Fragment key={campaign._id}>
            <RewardMissionCard
              campaign={campaign}
            />
            {index < campaigns.length - 1 && <Divider sx={{ my: 2 }} />}
          </React.Fragment>
        ))}
      </MissionGroup>
    </Box>
  );
};

const MissionGroup: React.FC<MissionGroupProps> = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Box sx={{ mb: 2 }}>
      <Typography variant="h5">{title}</Typography>
    </Box>
    {children}
    <Divider sx={{ my: 2 }} />
  </Box>
);

const CompulsoryMission: React.FC<CompulsoryMissionProps> = ({ weeklyShowcaseCount, timeUntilNextWeek }) => {
  const { t } = useTranslation();

  return (
    <CompulsoryMissionCard
      title={t('mission.peopleLoveToSeeYourWork')}
      description={t('mission.uploadQualityShowcases')}
      progressCount={weeklyShowcaseCount}
      progressTarget={2}
      progressColor={weeklyShowcaseCount >= 2 ? 'darkgreen' : 'darkgoldenrod'}
      timeRemaining={timeUntilNextWeek}
      isCompleted={weeklyShowcaseCount >= 2}
    />
  );
};

const CompulsoryMissionCard: React.FC<CompulsoryMissionCardProps> = ({ title, description, progressCount, progressTarget, progressColor, timeRemaining, isCompleted }) => {
  const { t } = useTranslation();

  return (
    <Box className={`mission-card`}>
      <Stack className={`mission-description ${isCompleted ? 'completed' : ''}`} spacing={1}>
        <Box className="mission-card-title">
          <Typography variant="body1" component="strong">{title}</Typography>
        </Box>
        <Box className="mission-card-progress" sx={{ whiteSpace: 'pre-line'}}>
          {description}&nbsp;
          <Box component="span" sx={{ color: progressColor }}>
            ({progressCount}/{progressTarget})
          </Box>
        </Box>
        <Box className="mission-card-timer">
          {t('mission.timeRemaining')}: <Typography component="strong">{timeRemaining}</Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const RewardMissionCard: React.FC<RewardMissionCardProps> = ({ campaign }) => {
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showProgress, setShowProgress] = useState<boolean>(false);
  const [showTNC, setShowTNC] = useState<boolean>(false);
  const currentLanguage = i18n.language;
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const handleShow = (): void => setShowModal(true);
  const handleClose = (): void => setShowModal(false);
  const handleToggleProgress = (): void => setShowProgress(!showProgress);
  const handleShowTNC = (): void => setShowTNC(true);
  const handleCloseTNC = (): void => setShowTNC(false);
  return (
    <Box>
      <Stack spacing={2}>
        <Box sx={{ fontWeight: 'bold' }}>
          {campaign[currentLanguage === 'zh' ? 'zh_title' : 'title'] as string}
        </Box>
        <Box className="mission-card-description" sx={{ whiteSpace: 'pre-line'}}>
          {campaign[currentLanguage === 'zh' ? 'zh_description' : 'description'] as string}&nbsp;
        </Box>
        <Box>{t('mission.campaignEndDate')}: {new Date(campaign.end_date).toLocaleDateString()}</Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={handleShow}>
            {t('mission.rules')}
          </Button>
          <Button variant="contained" onClick={handleShowTNC}>
            {t('mission.tnc')}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
          {t('mission.progress')}:
          <IconButton onClick={handleToggleProgress} size="small">
            {showProgress ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        {showProgress && campaign.trigger_event === 'referral-b2b' && (
          <Stack className="mission-card-progress-container" spacing={1}>
            <Stack direction="row" className="mission-card-progress" justifyContent="space-between">
              <Box className="progress-name">
                {t('mission.numberOfReferredBusiness')}:
              </Box>
              <Box className="progress-value">
                {campaign.progress.referralB2BCount}
              </Box>
            </Stack>
            <Stack direction="row" className="mission-card-progress" justifyContent="space-between">
              <Box className="progress-name">
                {t('mission.numberOfReferredStylist')}:
              </Box>
              <Box className="progress-value">
                {campaign.progress.referralStylistCount}
              </Box>
            </Stack>
            <Stack direction="row" className="mission-card-progress" justifyContent="space-between">
              <Box className="progress-name">
                {t('mission.numberOfReferredStylistActive')}:
              </Box>
              <Box className="progress-value">
                {campaign.progress.consecutiveStylistCount}
              </Box>
            </Stack>
            <Stack direction="row" className="mission-card-progress" justifyContent="space-between">
              <Box className="progress-name">
                {t('mission.totalCreditAmount')}:
              </Box>
              <Box className="progress-value">
                {campaign.totalCreditAmount} HKD
              </Box>
            </Stack>
          </Stack>
        )}
        {showProgress && campaign.trigger_event === 'referral-b2c' && (
          <Stack className="mission-card-progress-container" spacing={1}>
            <Stack direction="row" className="mission-card-progress" justifyContent="space-between">
              <Box className="progress-name">
                {t('mission.numberOfReferredCustomer')}:
              </Box>
              <Box className="progress-value">
                {campaign.progress.referralB2CCount || 0}
              </Box>
            </Stack>
            <Stack direction="row" className="mission-card-progress" justifyContent="space-between">
              <Box className="progress-name">
                {t('mission.totalCreditAmount')}:
              </Box>
              <Box className="progress-value">
                {campaign.totalCreditAmount} HKD
              </Box>
            </Stack>
          </Stack>
        )}
      </Stack>

      <Dialog
        open={showTNC}
        onClose={handleCloseTNC}
        aria-labelledby="mission-tnc-dialog"
        fullScreen={isSmallScreen}
      >
        <Box sx={{
          width: isSmallScreen ? '100%' : 600,
          bgcolor: 'background.paper',
          // boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {t('mission.tnc')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <iframe src={campaign[currentLanguage === 'zh' ? 'zh_tnc_url' : 'tnc_url'] as string} width="100%" height="550px" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleCloseTNC}>
              {t('mission.close')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={showModal}
        onClose={handleClose}
        aria-labelledby="mission-rules-dialog"
        fullScreen={isSmallScreen}
      >
        <Box sx={{
          width: isSmallScreen ? '100%' : 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {t('mission.rules')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <ol>
              {(campaign[currentLanguage === 'zh' ? 'zh_rules' : 'rules'] as string[]).map((rule: string, index: number) => (
                <li key={index}>{rule}</li>
              ))}
            </ol>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleClose}>
              {t('mission.close')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Mission;
